import { useMemo } from "react";
import { ExternalLink, GraduationCap, HeartPulse, PiggyBank, Sparkles } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { PageHeader } from "@/components/layout/PageHeader";
import { useCashAllocation } from "@/hooks/useStrategy";
import { useFamilyProfile } from "@/hooks/useProfile";
import { useTaxProjection } from "@/hooks/useTax";
import { formatMoney, formatPercent } from "@/lib/utils";

const SOLO_401K_EMPLOYEE_DEFERRAL = 23_500;
const SOLO_401K_COMBINED_LIMIT = 70_000;
const HSA_FAMILY_LIMIT = 8_550;
const HSA_SINGLE_LIMIT = 4_300;
const WI_EDVEST_PER_BENEFICIARY = 4_260;

export function RetirementPage() {
  const cash = useCashAllocation();
  const family = useFamilyProfile();
  const projection = useTaxProjection();

  // Real, computed marginal rates from the engine. If the engine is blocked,
  // these are null and the math/display fall through to "—".
  const proj = projection.data?.projection;
  const blocked = proj?.hasBlockingGaps ?? false;
  const fedRate = blocked ? null : proj?.nextDollarFederalRate ?? null;
  const stateRate = blocked ? null : proj?.nextDollarStateRate ?? null;
  const combined =
    fedRate != null && stateRate != null ? fedRate + stateRate : null;
  const projectedSolo = cash.data?.plan?.projectedAnnualSolo401k ?? null;
  const employerRoom =
    projectedSolo != null ? Math.max(0, projectedSolo - SOLO_401K_EMPLOYEE_DEFERRAL) : null;

  const isFamilyHsa = family.data?.isHsaEligible ?? false;
  const hsaLimit = family.data?.hasSpouse ? HSA_FAMILY_LIMIT : HSA_SINGLE_LIMIT;

  const childCount = family.data?.children.length ?? 0;
  const edvestRoom = childCount * WI_EDVEST_PER_BENEFICIARY;

  const totalTaxSaved = useMemo(() => {
    if (fedRate == null || stateRate == null || combined == null) return null;
    if (projectedSolo == null) return null;
    const solo = projectedSolo * fedRate;
    const hsa = isFamilyHsa ? hsaLimit * combined : 0;
    const edvest = edvestRoom * stateRate;
    return solo + hsa + edvest;
  }, [projectedSolo, fedRate, isFamilyHsa, hsaLimit, combined, edvestRoom, stateRate]);

  return (
    <>
      <PageHeader
        eyebrow="Strategy / Retirement"
        title="Retirement maximization"
        description="Solo 401(k), HSA, and 529 — the three best accounts in the U.S. tax code for your situation."
        actions={
          totalTaxSaved != null ? (
            <Badge tone="success" className="gap-1">
              <Sparkles className="h-3 w-3" />
              Combined potential: {formatMoney(totalTaxSaved)} / yr
            </Badge>
          ) : (
            <Badge tone="warning" className="gap-1">
              <Sparkles className="h-3 w-3" />
              Add Tax Intake to estimate savings
            </Badge>
          )
        }
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card id="solo401k">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <PiggyBank className="h-4 w-4 text-primary" />
              Solo 401(k)
            </CardTitle>
            <CardDescription>Establish by 12/31 to contribute for the current year (SECURE 2.0).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Employee deferral limit (2025)" value={formatMoney(SOLO_401K_EMPLOYEE_DEFERRAL)} />
            <Row label="Employer profit-share room" value={formatMoney(employerRoom)} />
            <Row label="Combined limit" value={formatMoney(SOLO_401K_COMBINED_LIMIT)} />
            <Row label="Your projected target" value={formatMoney(projectedSolo)} />
            <Row
              label="Approx tax saved"
              value={
                fedRate != null && projectedSolo != null
                  ? formatMoney(projectedSolo * fedRate)
                  : "—"
              }
              hint={fedRate != null ? `@ ${formatPercent(fedRate)}` : "Add intake"}
            />
            <p className="pt-2 text-xs text-muted-foreground">
              Mega Backdoor Roth requires a provider that supports after-tax contributions + in-plan
              Roth conversions. Set this in <strong>Settings → Mega Backdoor Roth available</strong>.
            </p>
            <a
              href="https://www.irs.gov/retirement-plans/one-participant-401k-plans"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 pt-2 text-xs text-muted-foreground hover:text-foreground"
            >
              IRS guidance <ExternalLink className="h-3 w-3" />
            </a>
          </CardContent>
        </Card>

        <Card id="hsa">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <HeartPulse className="h-4 w-4 text-primary" />
              HSA
            </CardTitle>
            <CardDescription>
              {isFamilyHsa ? "Family HDHP coverage on file" : "Mark HSA-eligible in People to enable"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row
              label={family.data?.hasSpouse ? "Family limit (2025)" : "Single limit (2025)"}
              value={formatMoney(hsaLimit)}
            />
            <Row
              label="Approx tax saved"
              value={
                isFamilyHsa && combined != null
                  ? formatMoney(hsaLimit * combined)
                  : "—"
              }
              hint={combined != null ? `@ ${formatPercent(combined)}` : "Add intake"}
            />
            <p className="pt-2 text-xs text-muted-foreground">
              Triple tax advantage: deductible in, tax-free growth, tax-free out for qualified
              medical. Best long-term move is to invest the balance and pay medical out of pocket.
            </p>
            <a
              href="https://www.irs.gov/publications/p969"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 pt-2 text-xs text-muted-foreground hover:text-foreground"
            >
              IRS Pub 969 <ExternalLink className="h-3 w-3" />
            </a>
          </CardContent>
        </Card>

        <Card id="529">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <GraduationCap className="h-4 w-4 text-primary" />
              Edvest 529 (WI)
            </CardTitle>
            <CardDescription>{childCount} {childCount === 1 ? "beneficiary" : "beneficiaries"}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Per-beneficiary deduction" value={formatMoney(WI_EDVEST_PER_BENEFICIARY)} />
            <Row label="Household capacity" value={formatMoney(edvestRoom)} />
            <Row
              label="WI tax saved"
              value={
                stateRate != null
                  ? formatMoney(edvestRoom * stateRate)
                  : "—"
              }
              hint={stateRate != null ? `@ ${formatPercent(stateRate)}` : "Add intake"}
            />
            <p className="pt-2 text-xs text-muted-foreground">
              Useful as a state-tax sponge once kids are entered. Federal tax-free growth applies if
              used for qualified education expenses; recent SECURE 2.0 changes allow limited rollover
              to Roth IRA.
            </p>
            <a
              href="https://www.edvest.com/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 pt-2 text-xs text-muted-foreground hover:text-foreground"
            >
              Edvest <ExternalLink className="h-3 w-3" />
            </a>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Order of operations</CardTitle>
          <CardDescription>For a high-W2 / Schedule-C household, the priority is roughly:</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            1. <strong className="text-foreground">HSA</strong> if HDHP — best dollar-for-dollar
            tax efficiency in the code.
          </p>
          <p>
            2. <strong className="text-foreground">Solo 401(k) employee deferral</strong> ($23,500)
            — instant {formatPercent(fedRate)} federal cut.
          </p>
          <p>
            3. <strong className="text-foreground">Solo 401(k) employer profit-share</strong> — up
            to {formatMoney(employerRoom)} more.
          </p>
          <p>
            4. <strong className="text-foreground">Mega Backdoor Roth</strong> via after-tax
            contributions if your provider supports it.
          </p>
          <p>
            5. <strong className="text-foreground">Edvest 529</strong> for state deduction;
            spillover from kid wages.
          </p>
          <p>
            6. <strong className="text-foreground">Custodial Roth IRA for kids</strong> funded by
            their W-2 wages from the business.
          </p>
        </CardContent>
      </Card>
    </>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border/40 pb-1.5 last:border-b-0">
      <span className="text-muted-foreground">
        {label}
        {hint && <span className="ml-1 text-xs">({hint})</span>}
      </span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
